import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";

// Server-safe styling contract. Keep this module free of client directives so
// Server Components can call buttonVariants without crossing an RSC boundary.
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-ds-full font-medium",
    "text-ds-body-sm",
    "transition-[background-color,border-color,box-shadow,transform,opacity] duration-[180ms] [transition-timing-function:cubic-bezier(0.2,0.8,0.2,1)]",
    "focus-visible:outline-none",
    "active:scale-[0.97] disabled:pointer-events-none disabled:opacity-40",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-pulse-700 text-grey-0 hover:bg-pulse-700 active:bg-pulse-800 disabled:bg-grey-200 disabled:text-grey-400 focus-visible:shadow-[0_0_0_1px_var(--color-grey-0),0_0_0_2px_var(--color-pulse-700)]",
        destructive:
          "text-grey-0 bg-red-600 hover:bg-red-700 focus-visible:shadow-[0_0_0_1px_var(--color-grey-0),0_0_0_2px_var(--color-red-600)] active:bg-red-800",
        secondary:
          "border-grey-100 bg-grey-0 text-grey-950 hover:border-grey-200 active:border-grey-400 focus-visible:border-grey-400 border focus-visible:shadow-[0_0_0_1px_var(--color-grey-0),0_0_0_2px_var(--color-pulse-700)]",
        "secondary-gradient-hover": "button-secondary-gradient-hover",
        "secondary-gradient-shadow-always-on":
          "button-secondary-gradient-shadow-always-on",
        "secondary-overlay":
          "border-grey-100 text-grey-0 border bg-white/10 hover:bg-white/15 focus-visible:shadow-[0_0_0_1px_var(--color-grey-0),0_0_0_2px_var(--color-pulse-700)] active:bg-white/20",
        tertiary:
          "bg-grey-950 text-grey-0 hover:bg-grey-800 active:bg-grey-950 focus-visible:shadow-[0_0_0_1px_var(--color-grey-0),0_0_0_2px_var(--color-grey-950)]",
        "tertiary-inverted":
          "bg-grey-0 text-grey-950 hover:bg-grey-100 active:bg-grey-200 focus-visible:shadow-[0_0_0_1px_var(--color-grey-950),0_0_0_2px_var(--color-grey-0)]",
        outline:
          "border-grey-300 text-grey-900 hover:bg-grey-50 active:bg-grey-100 disabled:border-grey-200 disabled:text-grey-400 border bg-transparent",
        ghost:
          "text-grey-900 hover:bg-pulse-50 active:bg-pulse-100 disabled:text-grey-400",
        link: "text-pulse-700 disabled:text-grey-400 underline-offset-4 hover:underline",
      },
      size: {
        default: "h-8 px-4 [&_svg]:size-3.5",
        sm: "h-7 px-3 [&_svg]:size-3",
        lg: "h-11 px-6 [&_svg]:size-4",
        xl: "text-ds-body-md h-[52px] px-7 [&_svg]:size-5",
        icon: "h-9 w-9 [&_svg]:size-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

type ButtonVariantProps = VariantProps<typeof buttonVariants>;

export { buttonVariants };
export type { ButtonVariantProps };
